import { useMemo } from "react";

/**
 * Renders a minimal markdown subset used by site content:
 *  - blank lines split paragraphs
 *  - lines starting with "## " become H3 headings
 *  - consecutive lines starting with "- " become a bullet list
 */
export default function RichText({ text, className = "", testId }) {
  const blocks = useMemo(() => parseBlocks(text || ""), [text]);
  return (
    <div className={className} data-testid={testId}>
      {blocks.map((b, i) => {
        if (b.type === "heading") {
          return (
            <h3
              key={i}
              className="font-serif text-2xl md:text-3xl text-ink-800 mt-12 mb-5 tracking-tight"
            >
              {b.text}
            </h3>
          );
        }
        if (b.type === "list") {
          return (
            <ul key={i} className="my-5 space-y-2.5">
              {b.items.map((it, j) => (
                <li key={j} className="flex gap-3 text-ink-800/85 leading-relaxed">
                  <span className="mt-2.5 inline-block w-2 h-px bg-gold-500 shrink-0" />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="my-4 text-ink-800/85 leading-[1.85] tracking-[0.005em]">
            {b.text}
          </p>
        );
      })}
    </div>
  );
}

function parseBlocks(raw) {
  const blocks = [];
  const chunks = raw.split(/\n\s*\n/);
  for (const chunk of chunks) {
    const lines = chunk.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) continue;
    if (lines[0].startsWith("## ")) {
      blocks.push({ type: "heading", text: lines[0].replace(/^##\s+/, "") });
      const rest = lines.slice(1).join(" ").trim();
      if (rest) blocks.push({ type: "paragraph", text: rest });
      continue;
    }
    if (lines.every((l) => l.startsWith("- "))) {
      blocks.push({ type: "list", items: lines.map((l) => l.replace(/^-\s+/, "")) });
      continue;
    }
    blocks.push({ type: "paragraph", text: lines.join(" ") });
  }
  return blocks;
}
